import React, { useState, useEffect, useCallback } from "react";
import { DialogBox } from "@/components/common/DialogBox";
import SearchDialogBox from "@/components/common/SearchDialogBox";
import api from "@/axios";
import { routes } from "@/utils/urls";
import { DataTable } from "@/components/common/Table";
import DialogDropdown from "@/components/common/DialogDropdown";
import {
  ApiResponse,
  DrugClassOption,
  MedicationFillHistory,
  MedicationFillistoryDialogProps,
  RawMedicationData,
  TimeRangeOption,
} from "./medicationFillHistory";
import MedicationFillHistoryLightIcon from "@/assets/svg/medicationFillHistoryLightIcon.svg";
import MedicationFillHistoryDarkIcon from "@/assets/svg/medicationFillHistoryDarkIcon.svg";
import { columns } from "./MedicationFillHistoryDialogColumn";
import { useDebounce } from "@/hooks/useDebounce";

const MedicationFillHistoryDialog: React.FC<
  MedicationFillistoryDialogProps
> = ({ open, onOpenChange, memberId }) => {
  const [searchValue, setSearchValue] = useState("");
  const [allMedication, setAllMedication] = useState<MedicationFillHistory[]>(
    []
  );
  const [loading, setLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMoreData, setHasMoreData] = useState(true);

  const [startDate, setStartDate] = useState<string | undefined>(undefined);
  const [endDate, setEndDate] = useState<string | undefined>(undefined);
  const [timeRange, setTimeRange] = useState("all");
  const [drugClass, setDrugClass] = useState("all");
  const [drugClassOptions, setDrugClassOptions] = useState<DrugClassOption[]>([
    { label: "All Classes", value: "all" },
  ]);
  const debouncedSearchValue = useDebounce(searchValue, 1000); // Debounce search value

  const rowsPerPage = 20;

  const timeRangeOptions: TimeRangeOption[] = [
    { label: "All Time", value: "all" },
    { label: "1 Month", value: "30" },
    { label: "2 Months", value: "60" },
    { label: "3 Months", value: "90" },
    { label: "4 Months", value: "120" },
    { label: "5 Months", value: "150" },
    { label: "6 Months", value: "180" },
    { label: "7 Months", value: "210" },
    { label: "8 Months", value: "240" },
    { label: "9 Months", value: "270" },
    { label: "10 Months", value: "300" },
    { label: "11 Months", value: "330" },
    { label: "12 Months", value: "365" },
  ];

  // Fetch drug classes from API
  const fetchDrugClasses = useCallback(async () => {
    try {
      const url = routes.cards.drugClases;
      const res = await api.get<{ status: number; data: string[] }>(url);

      if (res.data && Array.isArray(res.data.data)) {
        const formattedOptions: DrugClassOption[] = [
          { label: "All Classes", value: "all" },
          ...res.data.data.map((drugClass) => ({
            label: drugClass,
            value: drugClass,
          })),
        ];
        setDrugClassOptions(formattedOptions);
      }
    } catch (err) {
      console.error("Error fetching drug classes:", err);
      // Keep default "All Classes" option if API fails
    }
  }, []);

  const handleTimeRangeChange = (value: {
    days: string;
    startDate: string;
    endDate: string;
  }) => {
    if (value.days === "all") {
      setTimeRange("all");
      setStartDate(undefined);
      setEndDate(undefined);
    } else {
      setTimeRange(value.days);
      setStartDate(value.startDate);
      setEndDate(value.endDate);
    }
  };

  const handleDrugClassChange = (value: {
    days: string;
    startDate: string;
    endDate: string;
  }) => {
    // For drug class dropdown, we only use the 'days' field as the value
    setDrugClass(value.days);
  };

  const fetchMedicationFillHistory = useCallback(
    async (pageNumber: number = 1, append: boolean = false) => { // NOSONAR
      try {
        if (append) {
          setIsLoadingMore(true);
        } else {
          setLoading(true);
          setAllMedication([]);
          setHasMoreData(true);
        }

        const url = routes.cards.medicationFillHistory(memberId);

        const requestBody: {
          view: string;
          searchKey?: string;
          page: number;
          limit: number;
          days?: string;
          startDate?: string;
          endDate?: string;
          drugClass?: string;
        } = {
          view: "list",
          page: pageNumber,
          limit: rowsPerPage,
        };

        // FIXED: Only check debouncedSearchValue, not searchValue
        if (debouncedSearchValue.trim()) {
          requestBody.searchKey = debouncedSearchValue.trim();
        }

        if (timeRange !== "all") {
          requestBody.days = timeRange;
          if (startDate) requestBody.startDate = startDate;
          if (endDate) requestBody.endDate = endDate;
        }

        if (drugClass !== "all") {
          requestBody.drugClass = drugClass;
        }

        const res = await api.post<ApiResponse>(url, requestBody);

        let rawData: RawMedicationData[] = [];

        if (Array.isArray(res.data)) {
          rawData = res.data;
        } else if (res.data && typeof res.data === "object") {
          if ("data" in res.data && Array.isArray(res.data.data)) {
            rawData = res.data.data;
          } else if ("result" in res.data && Array.isArray(res.data.result)) {
            rawData = res.data.result;
          }
        }

        const transformedData: MedicationFillHistory[] = rawData.map(
          (item: RawMedicationData, index: number) => ({
            id: (pageNumber - 1) * rowsPerPage + index + 1,
            drug: item.drug,
            fillDate: item.fillDate,
            daysSupply: item.daysSupply,
            prescriber: item.prescriber,
            pharmacy: item.pharmacy,
            drugType: item.drugType,
            drugStrength: item.drugStrength,
            class: item.class,
          })
        );

        if (append) {
          setAllMedication((prev) => [...prev, ...transformedData]);
        } else {
          setAllMedication(transformedData);
        }

        setHasMoreData(transformedData.length === rowsPerPage);
      } catch (err) {
        console.error("API Error fetching medication fill history:", err);
        // const error = err as { response?: { data?: { message?: string } }; message?: string };
        // const errorMessage = error?.response?.data?.message || error?.message || "Failed to fetch medication fill history.";
        // setError(errorMessage);
        setHasMoreData(false);
      } finally {
        setLoading(false);
        setIsLoadingMore(false);
      }
    },
    // FIXED: Removed searchValue from dependencies, only use debouncedSearchValue
    [memberId, debouncedSearchValue, timeRange, startDate, endDate, drugClass]
  );

  const loadMoreData = useCallback(async () => {
    if (!hasMoreData || isLoadingMore) return;
    const nextPage = Math.floor(allMedication.length / rowsPerPage) + 1;
    await fetchMedicationFillHistory(nextPage, true);
  }, [
    hasMoreData,
    isLoadingMore,
    allMedication.length,
    fetchMedicationFillHistory,
  ]);

  const handleScroll = useCallback(
    ({
      scrollTop,
      scrollHeight,
      clientHeight,
    }: {
      scrollTop: number;
      scrollHeight: number;
      clientHeight: number;
    }) => {
      const isNearBottom = scrollHeight - scrollTop <= clientHeight + 50;
      if (isNearBottom && hasMoreData && !isLoadingMore && !loading) {
        loadMoreData();
      }
    },
    [hasMoreData, isLoadingMore, loading, loadMoreData]
  );

  // Fetch drug classes when dialog opens
  useEffect(() => {
    if (open) {
      fetchDrugClasses();
    }
  }, [open, fetchDrugClasses]);

  useEffect(() => {
    if (!open) {
      setSearchValue("");
      setTimeRange("all");
      setDrugClass("all");
      setStartDate(undefined);
      setEndDate(undefined);
      setAllMedication([]);
      setHasMoreData(true);
    }
  }, [open]);

  // FIXED: Combined into single useEffect with proper dependencies
  useEffect(() => {
    if (!open) return;
    fetchMedicationFillHistory(1, false);
  }, [
    open,
    timeRange,
    startDate,
    endDate,
    drugClass,
    debouncedSearchValue,
    fetchMedicationFillHistory,
  ]);

  return (
    <DialogBox
      open={open}
      onOpenChange={onOpenChange}
      className='max-w-[1300px]'
      icon={
        <>
          <img
            src={MedicationFillHistoryLightIcon}
            alt='Medication Fill History'
            className='w-4 h-4 block dark:hidden'
          />
          <img
            src={MedicationFillHistoryDarkIcon}
            alt='Medication Fill History'
            className='w-4 h-4 hidden dark:block'
          />
        </>
      }
      iconAlt='Medication Fill History'
      title='Medication Fill History'>
      <div className='flex flex-col max-h-[calc(85vh-100px)] min-h-[500px]'>
        <div className='flex-shrink-0 mb-4 space-y-3'>
          <div className='flex flex-col gap-3'>
            <div className='flex items-center gap-3 justify-between'>
              <SearchDialogBox
                value={searchValue}
                onChange={setSearchValue}
                placeholder='Search by Generic Drug Name'
                width='368px'
                smWidth='240px'
              />
              <div className='flex items-center gap-3 lg:gap-[16px] lg:ml-auto'>
                <div style={{ width: "180px", height: "40px" }}>
                  <DialogDropdown
                    options={timeRangeOptions}
                    value={timeRange}
                    onChange={handleTimeRangeChange}
                    icon='calendar-line'
                  />
                </div>
                <div className='hidden md:block w-[230px]'>
                  <DialogDropdown
                    options={drugClassOptions}
                    value={drugClass}
                    onChange={handleDrugClassChange}
                    placeholder='Select Drug Class'
                  />
                </div>
              </div>
            </div>
            <div className='w-[230px] md:hidden'>
              <DialogDropdown
                options={drugClassOptions}
                value={drugClass}
                onChange={handleDrugClassChange}
                placeholder='Select Drug Class'
              />
            </div>
          </div>
        </div>

        <div className='flex-1 overflow-hidden'>
          <div className='border rounded-lg h-full'>
            <DataTable<MedicationFillHistory>
              data={{ data: allMedication, total: allMedication.length }}
              columns={columns}
              height={allMedication.length === 0 ? "440px" : "auto"}
              maxHeight={"440px"}
              isLoading={loading}
              isLoadingMore={isLoadingMore}
              hasMoreData={hasMoreData}
              allData={allMedication}
              onScroll={handleScroll}
              skeletonCount={10}
            />
          </div>
        </div>
      </div>
    </DialogBox>
  );
};

export default MedicationFillHistoryDialog;
